/**
 * RecurringRevenueWidget
 * ----------------------
 * Dashboard card showing total weekly / monthly / annual recurring
 * revenue from all active Stripe subscriptions. Sachin spec 2026-02-17.
 *
 * Polls /api/coach/recurring-revenue. Renders 0-state cleanly so a
 * fresh account with no subs yet doesn't show garbage numbers.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Zap, AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) => {
    const v = (cents || 0) / 100;
    return v >= 1000
        ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
        : `$${v.toFixed(2)}`;
};

const INTERVAL_LABEL = {
    week: "weekly",
    month: "monthly",
    year: "yearly",
    day: "daily",
};

export default function RecurringRevenueWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/coach/recurring-revenue`, { withCredentials: true });
            setData(r.data || null);
        } catch {
            setData(null);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <div data-testid="recurring-revenue-widget-loading" className="border border-zinc-800 bg-zinc-950/40 px-4 py-3 flex items-center gap-2 text-xs text-zinc-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading recurring revenue…
            </div>
        );
    }
    if (!data) return null;

    const isEmpty = data.count === 0;

    return (
        <div
            data-testid="recurring-revenue-widget"
            className="border border-emerald-700/40 bg-gradient-to-br from-emerald-950/50 to-zinc-950 p-4"
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-emerald-400" />
                    <span className="text-[10px] tracking-[0.28em] uppercase text-emerald-300 font-display font-bold">Recurring Revenue</span>
                </div>
                {data.ending_soon > 0 && (
                    <span
                        data-testid="recurring-revenue-ending-soon"
                        className="inline-flex items-center gap-1 text-[9px] tracking-[0.18em] uppercase text-amber-300 border border-amber-700/60 bg-amber-900/30 px-1.5 py-0.5"
                        title="Subscriptions cancelled and ending at next renewal"
                    >
                        <AlertTriangle className="w-2.5 h-2.5" />
                        {data.ending_soon} ending
                    </span>
                )}
            </div>

            {isEmpty ? (
                <div className="mt-3 text-xs text-zinc-500" data-testid="recurring-revenue-empty">
                    No active subscriptions yet — start one from any member&apos;s profile.
                </div>
            ) : (
                <>
                    <div className="mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                        <div data-testid="recurring-revenue-weekly">
                            <div className="font-display text-3xl text-white font-bold tracking-tight">
                                {fmtMoney(data.weekly_cents)}
                                <span className="text-xs text-zinc-400 font-normal ml-1.5">/wk</span>
                            </div>
                        </div>
                        <div className="text-[11px] text-zinc-400" data-testid="recurring-revenue-monthly">
                            ≈ <span className="text-emerald-300 font-semibold">{fmtMoney(data.monthly_cents)}</span>/mo
                        </div>
                        <div className="text-[11px] text-zinc-400" data-testid="recurring-revenue-annual">
                            ≈ <span className="text-emerald-300 font-semibold">{fmtMoney(data.annual_cents)}</span>/yr
                        </div>
                    </div>
                    <div className="mt-2 text-[10px] tracking-[0.18em] uppercase text-zinc-500" data-testid="recurring-revenue-count">
                        {data.count} active sub{data.count === 1 ? "" : "s"}
                        {data.by_cadence.length > 1 && " · "}
                        {data.by_cadence.length > 1 && data.by_cadence.map((c, i) => (
                            <span key={c.interval}>
                                {i > 0 ? " · " : ""}
                                {c.count} {INTERVAL_LABEL[c.interval] || c.interval}
                            </span>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
